import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CustomersImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setResult(null); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/customers/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "tzat-customers-import-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
    }
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a .xlsx file first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/customers/import/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.imported} imported${data.imported_incomplete ? ` (${data.imported_incomplete} incomplete)` : ""}`);
      onImported?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadErrorReport = () => {
    if (!result?.errors?.length) return;
    const rows = [["Row", "Customer", "Error", "Suggested fix"], ...result.errors.map((e) => [e.row, e.customer, e.error, e.fix || ""])];
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "tzat-customers-import-errors.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Import Customers</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <section className="border border-[#3A4640] rounded-md p-4 bg-[#0B0F0C]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-[#39FF14] flex items-center justify-center text-[#0B0F0C] shrink-0">
                <FileSpreadsheet size={18} />
              </div>
              <div className="flex-1">
                <div className="font-heading font-bold text-[#F8F9FA]">1. Download the template</div>
                <div className="text-xs text-[#8A8F98] mt-0.5">A pre-formatted Excel workbook with all customer columns, dropdowns for Type and Status, and a sample row.</div>
              </div>
              <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn" className="border-[#39FF14]/40 text-[#39FF14] hover:bg-green-50">
                <Download size={14} className="mr-2" /> Template
              </Button>
            </div>
          </section>

          <section className="border border-[#3A4640] rounded-md p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-md bg-yellow-500 flex items-center justify-center text-[#0B0F0C] shrink-0">
                <Upload size={18} />
              </div>
              <div className="flex-1">
                <div className="font-heading font-bold text-[#F8F9FA]">2. Upload your completed file</div>
                <div className="text-xs text-[#8A8F98] mt-0.5">Missing required fields will still import — the row is tagged as <b>Incomplete Profile</b>. Duplicate mobile numbers or TZAT numbers are skipped.</div>
              </div>
            </div>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              data-testid="import-file-input"
              className="block w-full text-sm text-[#F8F9FA] file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#39FF14] file:text-[#0B0F0C] file:font-medium hover:file:bg-[#14532D]"
            />
            <div className="mt-3">
              <Button
                onClick={upload}
                disabled={!file || uploading}
                data-testid="import-upload-btn"
                className="bg-yellow-500 hover:bg-yellow-600 text-[#0B0F0C]"
              >
                {uploading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
                {uploading ? "Importing…" : "Start import"}
              </Button>
            </div>
          </section>

          {result && (
            <section className="border border-[#3A4640] rounded-md p-4" data-testid="import-summary">
              <div className="font-heading font-bold text-[#F8F9FA] mb-3">Import summary</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <SummaryCell label="Processed" value={result.processed} colour="#0f2417" />
                <SummaryCell label="Imported" value={result.imported} colour="#166534" icon={<CheckCircle2 size={14} />} />
                <SummaryCell label="Incomplete" value={result.imported_incomplete} colour="#D97706" icon={<AlertTriangle size={14} />} />
                <SummaryCell label="Skipped / errors" value={result.skipped} colour="#991b1b" icon={<XCircle size={14} />} />
              </div>
              {result.errors?.length > 0 && (
                <>
                  <div className="text-xs font-heading uppercase tracking-wider text-[#8A8F98] mb-2">Errors ({result.errors.length})</div>
                  <div className="max-h-48 overflow-y-auto border border-[#3A4640] rounded text-xs" data-testid="import-error-list">
                    <table className="w-full">
                      <thead className="bg-[#0B0F0C] text-[#8A8F98]">
                        <tr>
                          <th className="text-left px-2 py-1.5">Row</th>
                          <th className="text-left px-2 py-1.5">Customer</th>
                          <th className="text-left px-2 py-1.5">Error</th>
                          <th className="text-left px-2 py-1.5">Fix</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.errors.slice(0, 100).map((e, i) => (
                          <tr key={i} className="border-t border-[#3A4640]">
                            <td className="px-2 py-1.5 font-mono">{e.row}</td>
                            <td className="px-2 py-1.5">{e.customer}</td>
                            <td className="px-2 py-1.5 text-red-300">{e.error}</td>
                            <td className="px-2 py-1.5 text-[#8A8F98]">{e.fix}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-2">
                    <Button variant="outline" size="sm" onClick={downloadErrorReport} data-testid="download-error-report">
                      <Download size={13} className="mr-2" /> Download error report (CSV)
                    </Button>
                  </div>
                </>
              )}
            </section>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCell({ label, value, colour, icon }) {
  return (
    <div className="border border-[#3A4640] rounded p-3 bg-[#121815]" style={{ borderLeft: `4px solid ${colour}` }}>
      <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading font-semibold flex items-center gap-1">{icon} {label}</div>
      <div className="font-heading text-2xl font-extrabold text-[#F8F9FA] mt-0.5">{value}</div>
    </div>
  );
}
